import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';

import { useCloudSynced } from '@/hooks';

const SAVED_KEY = 'saved_signs';
const RECENT_KEY = 'recent_signs';

const RECENT_MAX = 8;

function parseCodes(raw: unknown): string[] | null {
  if (typeof raw !== 'string' || raw === '') return null;
  try {
    const arr: unknown = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter((x): x is string => typeof x === 'string') : null;
  } catch {
    return null;
  }
}

interface SavedSignsState {
  saved: string[];
  recent: string[];
  has: (code: string) => boolean;
  toggle: (code: string) => void;
  visit: (code: string) => void;
}

const SavedSignsContext = createContext<SavedSignsState>({
  saved: [],
  recent: [],
  has: () => false,
  toggle: () => {},
  visit: () => {},
});

export function useSavedSigns(): SavedSignsState {
  return useContext(SavedSignsContext);
}

export function SavedSignsProvider({ children }: { children: ReactNode }) {
  const [saved, setSaved] = useCloudSynced<string[]>(SAVED_KEY, parseCodes, [], JSON.stringify);
  const [recent, setRecent] = useCloudSynced<string[]>(RECENT_KEY, parseCodes, [], JSON.stringify);

  const toggle = useCallback(
    (code: string) =>
      setSaved((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code])),
    [setSaved],
  );

  const visit = useCallback(
    (code: string) =>
      setRecent((prev) => [code, ...prev.filter((c) => c !== code)].slice(0, RECENT_MAX)),
    [setRecent],
  );

  const value = useMemo<SavedSignsState>(() => {
    const set = new Set(saved);
    return { saved, recent, has: (code) => set.has(code), toggle, visit };
  }, [saved, recent, toggle, visit]);

  return <SavedSignsContext.Provider value={value}>{children}</SavedSignsContext.Provider>;
}
